import type { EditorReactComponent } from '@wix/component-protocol/schema';

export type ResolvedManifestCacheEntry = {
  resolvedManifest: EditorReactComponent;
  componentVersion: string;
  protocolVersion: string;
  language?: string;
  labelMappingVersion?: string;
};

export type ResolvedManifestCacheValidationInput = Pick<
  ResolvedManifestCacheEntry,
  'componentVersion' | 'protocolVersion' | 'language' | 'labelMappingVersion'
>;

export type ResolvedManifestCacheContext = Pick<
  ResolvedManifestCacheEntry,
  'language' | 'labelMappingVersion'
>;

const DB_NAME = 'editor-platform-manifest-services';
const DB_VERSION = 1;
const STORE_NAME = 'resolved-manifests-v1';
const NON_CACHEABLE_COMPONENT_VERSION_TOKENS = new Set(['dev', 'unknown']);

export class ResolvedManifestPersistentCache {
  #dbPromise: Promise<IDBDatabase> | null = null;

  constructor(private readonly enabled: boolean) {}

  static extractComponentVersionToken(component: unknown): string {
    const candidate = component as
      | { version?: string | number | null }
      | undefined;

    if (!candidate) {
      return 'unknown';
    }

    const { version } = candidate;

    if (version === null || version === undefined || version === '') {
      return 'unknown';
    }

    return String(version);
  }

  isValid(
    entry: ResolvedManifestCacheEntry | null,
    validation: ResolvedManifestCacheValidationInput,
  ) {
    return (
      !!entry &&
      this.#isCacheableComponentVersion(validation.componentVersion) &&
      entry.componentVersion === validation.componentVersion &&
      entry.protocolVersion === validation.protocolVersion &&
      entry.language === validation.language &&
      entry.labelMappingVersion === validation.labelMappingVersion
    );
  }

  async get(compType: string): Promise<ResolvedManifestCacheEntry | null> {
    const value = await this.#execute('readonly', (store) =>
      store.get(compType),
    );

    return (value as ResolvedManifestCacheEntry | undefined) ?? null;
  }

  async getValidEntry(
    compType: string,
    validation: ResolvedManifestCacheValidationInput,
  ): Promise<ResolvedManifestCacheEntry | null> {
    if (!this.#isCacheableComponentVersion(validation.componentVersion)) {
      return null;
    }

    const entry = await this.get(compType);

    return this.isValid(entry, validation) ? entry : null;
  }

  async set(
    compType: string,
    value: ResolvedManifestCacheEntry,
  ): Promise<void> {
    if (!this.#isCacheableComponentVersion(value.componentVersion)) {
      return;
    }

    await this.#execute('readwrite', (store) => store.put(value, compType));
  }

  async delete(compType: string): Promise<void> {
    await this.#execute('readwrite', (store) => store.delete(compType));
  }

  async clear(): Promise<void> {
    await this.#execute('readwrite', (store) => store.clear());
  }

  async #execute<T>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T | undefined> {
    if (!this.#isAvailable()) {
      return undefined;
    }

    try {
      const db = await this.#getDb();
      const transaction = db.transaction(STORE_NAME, mode);
      const store = transaction.objectStore(STORE_NAME);
      const request = operation(store);
      const result = await this.#waitForRequest(request);
      await this.#waitForTransaction(transaction);
      return result;
    } catch {
      return undefined;
    }
  }

  #isAvailable() {
    return this.enabled && typeof indexedDB !== 'undefined';
  }

  #isCacheableComponentVersion(componentVersion: string) {
    return !NON_CACHEABLE_COMPONENT_VERSION_TOKENS.has(componentVersion);
  }

  #getDb() {
    if (!this.#dbPromise) {
      this.#dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME);
          }
        };

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          this.#dbPromise = null;
          reject(request.error);
        };
      });
    }

    return this.#dbPromise;
  }

  #waitForRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  #waitForTransaction(transaction: IDBTransaction): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }
}
